import React from "react";
import produce from "immer";
import moment from "moment";
import styled, { css } from "styled-components";
import { useMutation, queryCache } from "react-query";
import { Button, Row, Col, Checkbox, Popconfirm, Typography, Spin } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  MoreOutlined,
  BellOutlined,
} from "@ant-design/icons";
import { NotificationStateContext } from "context/UI/Notification/NotificationState";
import { apiAxios } from "services/HttpServices";
import { calculateDayDiffFromToday } from "lib/date";
import CreateDateline from "components/job/index/dateline/createDateline";
import EditDatelineModal from "components/job/index/modal/editDatelineModal";

const { Text } = Typography;

const deleteTask = async ({ jobId, datelineId }) => {
  try {
    const res = await apiAxios.delete(`/jobs/${jobId}/dateline/${datelineId}`);
    return res.status;
  } catch (e) {
    throw e.response.data.message;
  }
};

const markDatelineComplete = async ({ jobId, datelineId }) => {
  try {
    const res = await apiAxios.put(`/jobs/${jobId}/dateline/${datelineId}`, {
      completed: true,
      completed_at: moment().format(),
    });
    return {
      completed: res.data.data.completed,
      completed_at: res.data.data.completed_at,
    };
  } catch (e) {
    throw e.response.data.message;
  }
};

const markDatelineUncomplete = async ({ jobId, datelineId }) => {
  try {
    const res = await apiAxios.put(`/jobs/${jobId}/dateline/${datelineId}`, {
      completed: false,
      completed_at: null,
    });
    return {
      completed: res.data.data.completed,
      completed_at: res.data.data.completed_at,
    };
  } catch (e) {
    throw e.response.data.message;
  }
};

const DatelinesList = (props) => {
  // Component Props
  const { datelines, status, jobId, jobStatus } = props;

  // Component State
  const [modalVisible, setModalVisible] = React.useState(false);
  const [selectedDateline, setSelectedDateline] = React.useState({});
  const [error, setError] = React.useState("");
  const [showCreateDateline, setShowCreateDateline] = React.useState(false);

  const {
    state: notificationState,
    setSuccessMessage,
    clearSuccessMessage,
    setLoading,
    finishLoading,
  } = React.useContext(NotificationStateContext);

  // Component Function
  const confirmDeleteDateline = async (jobId, datelineId) => {
    setLoading(`deleteDateline-${datelineId}`);
    try {
      await mutateDelete({ jobId, datelineId });
      finishLoading(`deleteDateline-${datelineId}`);
      setSuccessMessage("Succesfully deleted dateline");
    } catch (error) {
      finishLoading(`deleteDateline-${datelineId}`);
      console.log(error);
    }
  };

  const handleOpenModal = (dateline) => {
    setSelectedDateline(dateline);
    setModalVisible(true);
  };

  const handleDatelineCheckbox = async (value, jobId, datelineId) => {
    if (value == true) {
      setLoading(`markDateline-${datelineId}`);
      try {
        await mutateCompleteDateline({ jobId, datelineId });
        finishLoading(`markDateline-${datelineId}`);
      } catch (error) {
        finishLoading(`markDateline-${datelineId}`);
        console.log(error);
      }
    } else if (value === false) {
      setLoading(`markDateline-${datelineId}`);
      try {
        await mutateUncompleteDateline({ jobId, datelineId });
        finishLoading(`markDateline-${datelineId}`);
      } catch (error) {
        finishLoading(`markDateline-${datelineId}`);
        console.log(error);
      }
    }
  };

  // Mutation
  const [mutateDelete] = useMutation(deleteTask, {
    onSuccess: (_, variables) => {
      queryCache.setQueryData(
        ["job-detail", variables.jobId.toString()],
        function (oldData) {
          const updatedDateline = produce(oldData, (draft) => {
            draft.dateline = draft.dateline.filter(
              (dateline) => dateline.id !== variables.datelineId
            );
          });

          return updatedDateline;
        }
      );
    },
    onError: (e) => {
      setError(e);
    },
  });

  const [mutateCompleteDateline] = useMutation(markDatelineComplete, {
    onSuccess: (dateline, variables) => {
      queryCache.setQueryData(
        ["job-detail", variables.jobId.toString()],
        function (oldData) {
          const datelineIndex = oldData.dateline.findIndex(
            (dateline) => dateline.id === variables.datelineId
          );

          const updatedDateline = produce(oldData, (draft) => {
            draft.dateline[datelineIndex].completed = dateline.completed;
            draft.dateline[datelineIndex].completed_at = dateline.completed_at;
          });

          return updatedDateline;
        }
      );
    },
    onError: (e) => {
      setError(e);
    },
  });

  const [mutateUncompleteDateline] = useMutation(markDatelineUncomplete, {
    onSuccess: (dateline, variables) => {
      queryCache.setQueryData(
        ["job-detail", variables.jobId.toString()],
        function (oldData) {
          const datelineIndex = oldData.dateline.findIndex(
            (dateline) => dateline.id === variables.datelineId
          );
          const updatedDateline = produce(oldData, (draft) => {
            draft.dateline[datelineIndex].completed = dateline.completed;
            draft.dateline[datelineIndex].completed_at = dateline.completed_at;
          });

          return updatedDateline;
        }
      );
    },
    onError: (e) => {
      setError(e);
    },
  });

  return (
    <>
      {status !== "loading" ? (
        <>
          <StyledChildRow>
            <Col span={24}>
              {datelines?.map((dateline) => {
                const dayDiff = calculateDayDiffFromToday(
                  moment(dateline.due_at)
                );
                return (
                  <StyledTasksList key={dateline.id}>
                    <Row style={{ width: "100%" }} align="middle">
                      <Col xs={16}>
                        <Checkbox
                          disabled={jobStatus !== "started" ? true : false}
                          checked={dateline.completed}
                          onChange={(e) =>
                            handleDatelineCheckbox(
                              e.target.checked,
                              jobId,
                              dateline.id
                            )
                          }
                        >
                          {notificationState.loading[
                            `markDateline-${dateline.id}`
                          ] && (
                            <Spin size="small" style={{ marginRight: "5px" }} />
                          )}
                          <Text>{dateline.title}</Text>
                        </Checkbox>
                      </Col>

                      <Col xs={8}>
                        <div
                          css={`
                            display: flex;
                            justify-content: flex-end;
                            align-items: center;
                          `}
                        >
                          {dateline.due_at && (
                            <StyledDueDateContainer
                              variant={
                                dayDiff < 10
                                  ? "danger"
                                  : dayDiff <= 20
                                  ? "warning"
                                  : "good"
                              }
                            >
                              <BellOutlined className="icon" />
                              <Text className="text">
                                {moment(dateline.due_at)
                                  .utc()
                                  .format("DD MMMM")}
                              </Text>
                            </StyledDueDateContainer>
                          )}

                          <Popconfirm
                            title="Are you sure delete this dateline?"
                            onConfirm={() =>
                              confirmDeleteDateline(
                                dateline.job_id,
                                dateline.id
                              )
                            }
                            onCancel={() => {
                              return;
                            }}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button
                              type="link"
                              size="small"
                              icon={<DeleteOutlined />}
                              loading={
                                notificationState.loading[
                                  `deleteDateline-${dateline.id}`
                                ]
                              }
                              css={`
                                color: ${(props) =>
                                  props.theme.colors.grey[700]};
                                margin-right: ${(props) =>
                                  props.theme.spacing[1]};
                              `}
                            />
                          </Popconfirm>
                          <Button
                            type="link"
                            size="small"
                            icon={<MoreOutlined />}
                            onClick={() => handleOpenModal(dateline)}
                            css={`
                              color: ${(props) => props.theme.colors.grey[700]};
                            `}
                          />
                        </div>
                      </Col>
                    </Row>
                  </StyledTasksList>
                );
              })}
            </Col>
            {showCreateDateline ? (
              <CreateDateline
                jobId={jobId}
                setShowCreateDateline={setShowCreateDateline}
              />
            ) : (
              <Col span={24}>
                <StyledTasksList
                  $add
                  onClick={() => setShowCreateDateline(true)}
                >
                  <StyledButton
                    shape="circle"
                    size="small"
                    icon={<PlusOutlined />}
                  />
                  <ShallowText>Add Dateline</ShallowText>
                </StyledTasksList>
              </Col>
            )}
          </StyledChildRow>
          <EditDatelineModal
            jobId={jobId}
            modalVisible={modalVisible}
            selectedDateline={selectedDateline}
            handleCloseModal={() => {
              setModalVisible(false);
            }}
          />
        </>
      ) : (
        <span>Hello</span>
      )}
    </>
  );
};

const StyledChildRow = styled(Row)`
  background-color: ${(props) => props.theme.colors.white};
  margin-top: ${(props) => props.theme.spacing[2]};
  ${(props) =>
    props.$newJob &&
    css`
      padding: ${(props) => props.theme.spacing[4]};
    `};
`;

const StyledTasksList = styled.div`
  padding: ${(props) => props.theme.spacing[2]};
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${(props) => props.theme.colors.grey[200]};
  ${(props) =>
    props.$add &&
    css`
      border-bottom: none;
      cursor: pointer;
    `};
  &:hover {
    background: ${(props) => props.theme.colors.grey[100]};
  }
`;

const StyledButton = styled(Button)`
  background: ${(props) => props.theme.colors.grey[300]};
  color: ${(props) => props.theme.colors.grey[600]};
  margin-right: ${(props) => props.theme.spacing[2]};
`;

const StyledDueDateContainer = styled.div`
  border-radius: ${(props) => props.theme.spacing[1]};
  display: flex;
  align-items: center;
  padding: 1px;
  margin-right: ${(props) => props.theme.spacing[2]};
  background-color: ${(props) =>
    props.variant === "good"
      ? props.theme.colors.green[200]
      : props.variant === "warning"
      ? props.theme.colors.yellow[200]
      : props.variant === "danger" && props.theme.colors.red[200]};
  border: 1px solid
    ${(props) =>
      props.variant === "good"
        ? props.theme.colors.green[400]
        : props.variant === "warning"
        ? props.theme.colors.yellow[400]
        : props.variant === "danger" && props.theme.colors.red[400]};
  .text {
    margin-right: 5px;
    font-size: 0.7rem;
    color: ${(props) =>
      props.variant === "good"
        ? props.theme.colors.green[700]
        : props.variant === "warning"
        ? props.theme.colors.yellow[700]
        : props.variant === "danger" && props.theme.colors.red[700]};
  }
  .icon {
    margin: 0 5px;
    color: ${(props) =>
      props.variant === "good"
        ? props.theme.colors.green[700]
        : props.variant === "warning"
        ? props.theme.colors.yellow[700]
        : props.variant === "danger" && props.theme.colors.red[700]};
  }
`;

const ShallowText = styled(Text)`
  color: ${(props) => props.theme.colors.grey[500]};
  font-size: ${(props) => props.theme.fontSize["sm"]};
`;

export default DatelinesList;
